import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import { Ban, Check, ChevronDown, Ellipsis, Loader2, Pause, Pencil, Play, Plus, Search, Settings, SlidersHorizontal, Trash2, TriangleAlert, Users } from 'lucide-react';
import { styles } from '../../../styles/themes/style';
import { ICampaign, CampaignStatus, CampaignLeadSource, CampaignLeadStatus, ICampaignLeadStatusCounts, ISenderIdentity, CAMPAIGN_START_NO_LEADS_HINT, sumEnrolledLeads } from '../../../interfaces/models/campaign';
import { useCampaignsRequest } from '../../../services/apis/campaigns/useCampaignsRequest';
import { useAppActions } from '../../../stores/application/useAppActions';
import { useAuthStore } from '../../../stores/auth/useAuthStore';
import { AppRoutes } from '../../../interfaces/store/appStore';
import Row from '../../../components/Row';
import Text from '../../../components/Text';
import SvgFragment from '../../../fragments/SvgFragment';
import TooltipWrapper from '../../../components/TooltipWrapper';
import LoadingSpinner from '../../../components/LoadingSpinner';
import DropdownMenu from '../../../components/dropdown/DropdownMenu';
import { DEFAULT_SIM_CONFIG, ISimGlobalConfig, PROXY_UI_POLL_MS, SIM_EVENT } from '../hooks/useCampaignSimulation';
import { campaignMobile } from '../utils/campaignBreakpoints';
import CampaignPerformance from '../components/campaigns/performance/CampaignPerformance';
import { isAccountDisconnected } from '../components/campaigns/CampaignAccountReconnectBanner';
import { useSavedSenderFilter } from '../components/campaigns/useSavedSenderFilter';

const STATUS_LABELS: Record<CampaignStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  paused: 'Paused',
  canceled: 'Stopped',
};

type CampaignStatusFilter = CampaignStatus | 'all';

type CampaignsTab = 'campaigns' | 'performance';

const STATUS_FILTER_OPTIONS: { value: CampaignStatusFilter; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'canceled', label: 'Stopped' },
];


const SOURCE_LABELS: Record<CampaignLeadSource, string> = {
  leadsearch: 'LeadSearch',
  contacts: 'LD Contacts',
  csv: 'CSV',
  posts: 'Post',
  sn_lists: 'SN List',
  signals: 'Signals',
};

/** Leads that left the sequence with an answer or a final outcome. */
const FINISHED_LEAD_STATUSES: CampaignLeadStatus[] = ['replied', 'email_replied', 'converted', 'closed_lost'];

function formatUpdatedAgo(iso?: string): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '';
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'Updated just now';
  if (minutes < 60) return `Updated ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Updated ${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `Updated ${days}d ago`;
  try {
    return `Updated ${new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}`;
  } catch {
    return '';
  }
}

// The card shows where each lead is in its journey, not every step of the sequence:
// the per-step breakdown lives on the campaign's leads page.
function buildStats(c: ICampaign): { key: string; label: string; value: number }[] {
  const counts: Partial<ICampaignLeadStatusCounts> = c.statusCounts ?? {};
  const total = sumEnrolledLeads(c.statusCounts);
  const notStarted = counts.pending ?? 0;
  const finished = FINISHED_LEAD_STATUSES.reduce((sum, s) => sum + (counts[s] ?? 0), 0);
  const failed = counts.failed ?? 0;
  return [
    { key: 'total', label: 'Total', value: total },
    { key: 'not_started', label: 'Not started', value: notStarted },
    { key: 'in_sequence', label: 'In sequence', value: Math.max(0, total - notStarted - finished - failed) },
    { key: 'finished', label: 'Finished', value: finished },
    { key: 'failed', label: 'Failed', value: failed },
  ];
}

function formatSenderNames(names: string[]): string {
  if (names.length <= 2) return names.join(', ');
  return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
}

/** Edit (pen on a square) from the design; currentColor so it follows the menu item's state. */
const EditIcon = () => (
  <svg width="15" height="15" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M7 1.75H2.91667C2.60725 1.75 2.3105 1.87292 2.09171 2.09171C1.87292 2.3105 1.75 2.60725 1.75 2.91667V11.0833C1.75 11.3928 1.87292 11.6895 2.09171 11.9083C2.3105 12.1271 2.60725 12.25 2.91667 12.25H11.0833C11.3928 12.25 11.6895 12.1271 11.9083 11.9083C12.1271 11.6895 12.25 11.3928 12.25 11.0833V7" stroke="currentColor" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10.7188 1.53114C10.9508 1.29907 11.2656 1.1687 11.5938 1.1687C11.9219 1.1687 12.2367 1.29907 12.4688 1.53114C12.7008 1.7632 12.8312 2.07795 12.8312 2.40614C12.8312 2.73433 12.7008 3.04907 12.4688 3.28114L7.21117 8.53931C7.07265 8.6777 6.90154 8.779 6.71358 8.83389L5.03767 9.32389C4.98747 9.33853 4.93426 9.33941 4.88361 9.32643C4.83296 9.31345 4.78673 9.2871 4.74976 9.25013C4.71279 9.21316 4.68644 9.16693 4.67346 9.11627C4.66048 9.06562 4.66136 9.01242 4.676 8.96222L5.166 7.2863C5.22114 7.0985 5.32265 6.92759 5.46117 6.78931L10.7188 1.53114Z" stroke="currentColor" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Active campaigns first; within each group, newest first. */
function compareCampaigns(a: ICampaign, b: ICampaign): number {
  const activeDiff = Number(b.status === 'active') - Number(a.status === 'active');
  if (activeDiff) return activeDiff;
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

const CampaignSection: FunctionComponent = () => {
  const navigate = useNavigate();
  const { getCampaigns, updateCampaign, deleteCampaign, getSenderIdentities } = useCampaignsRequest();
  const { setToast } = useAppActions();
  const { store: { user } } = useAuthStore();
  const currentUserId = user?._id;

  const [campaigns, setCampaigns] = useState<ICampaign[]>([]);
  const [tab, setTab] = useState<CampaignsTab>('campaigns');
  const [identities, setIdentities] = useState<ISenderIdentity[]>([]);
  const [identitiesLoaded, setIdentitiesLoaded] = useState(false);
  const [disconnectedAccountIds, setDisconnectedAccountIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [stopId, setStopId] = useState<string | null>(null);
  const [rename, setRename] = useState<{ id: string; name: string } | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  // The campaign whose Start/Pause/Stop request is in flight — its button shows a spinner.
  const [statusChangingId, setStatusChangingId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<CampaignStatusFilter>('all');
  const [senderFilter, setSenderFilter] = useSavedSenderFilter('campaigns.senderFilter', identities, identitiesLoaded, currentUserId);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [senderMenuOpen, setSenderMenuOpen] = useState(false);

  const activeStatusLabel = STATUS_FILTER_OPTIONS.find(o => o.value === statusFilter)?.label ?? 'All statuses';

  const identityNames = useMemo(
    () => new Map(identities.map(i => [i._id, i.name || i.linkedin?.name || i.mail?.fromName || 'Unnamed sender'])),
    [identities],
  );

  // "(Me)" is the viewer's own LinkedIn profile only — they may have connected others.
  const ownProfileIds = useMemo(() => new Set(identities.filter(i => i.isMe).map(i => String(i._id))), [identities]);
  const senderLabel = (id: string) =>
    `${identityNames.get(id) ?? 'Unnamed sender'}${ownProfileIds.has(id) ? ' (Me)' : ''}`;

  /*
    The backend only writes identityIds once something resolves the campaign's senders
    (opening it, starting it), so a campaign nobody opened since senders shipped has
    none. Those are matched the way the backend would resolve them: proxy campaigns
    through their LinkedIn accounts, extension campaigns through the creator's own
    extension sender.
  */
  const campaignSenderIds = useCallback((c: ICampaign): string[] => {
    if (c.identityIds?.length) return c.identityIds.map(String);
    if (c.executionMethod === 'proxy') {
      const accountIds = (c.unipileConfig?.accountIds ?? []).map(String);
      return identities
        .filter(i => i.linkedin?.accountId && accountIds.includes(String(i.linkedin.accountId)))
        .map(i => String(i._id));
    }
    return identities
      .filter(i => i.linkedin?.kind === 'extension' && String(i.createdBy) === String(c.createdBy))
      .map(i => String(i._id));
  }, [identities]);

  const activeSenderLabel = senderFilter.length === 0
    ? 'All'
    : senderFilter.length === 1
      ? senderLabel(senderFilter[0])
      : `${senderFilter.length} selected`;

  /*
    Active campaigns first; within each group, newest first.

    The order is taken when the page loads and then held: pausing or starting a
    campaign (or the background poll seeing it change) must not make its card jump
    away from under the cursor. Campaigns the snapshot hasn't seen yet go on top.
  */
  const [orderIds, setOrderIds] = useState<string[]>([]);
  const filteredCampaigns = useMemo(() => {
    const position = new Map(orderIds.map((id, i) => [id, i]));
    const q = searchQuery.trim().toLowerCase();
    return campaigns
      .filter(c => {
        if (statusFilter !== 'all' && c.status !== statusFilter) return false;
        if (senderFilter.length) {
          // A campaign with no sender yet (a draft before its accounts are picked)
          // still belongs to whoever created it, so it follows that person's senders.
          const senderIds = campaignSenderIds(c);
          const matchIds = senderIds.length
            ? senderIds
            : identities.filter(i => String(i.createdBy) === String(c.createdBy)).map(i => String(i._id));
          if (!matchIds.some(id => senderFilter.includes(id))) return false;
        }
        if (!q) return true;
        const haystack = [c.name, c.description, c.createdByName]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      })
      .sort((a, b) => {
        const pa = position.get(a._id) ?? -1;
        const pb = position.get(b._id) ?? -1;
        if (pa !== pb) return pa - pb;
        return compareCampaigns(a, b);
      });
  }, [campaigns, orderIds, statusFilter, senderFilter, searchQuery, campaignSenderIds, identities]);

  const hasActiveFilters = statusFilter !== 'all' || senderFilter.length > 0 || searchQuery.trim().length > 0;

  const hasProxyRunning = useMemo(
    () => campaigns.some(
      c => c.executionMethod === 'proxy' && (c.status === 'active' || c.status === 'paused'),
    ),
    [campaigns],
  );

  // ── Simulation config ─────────────────────────────────────────────────────
  // When REACT_APP_SIM_HIDE_UI is set, the controls are hidden and parameters
  // are driven entirely by env vars (REACT_APP_SIM_INTERVAL_MIN/MAX, REACT_APP_SIM_LEADS_MIN/MAX).
  const hideSimUI = !!process.env.REACT_APP_SIM_HIDE_UI;
  const [simConfig, setSimConfig] = useState<ISimGlobalConfig>(DEFAULT_SIM_CONFIG);
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('campaignSimConfigChange', { detail: simConfig }));
  }, [simConfig]);
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => { void loadCampaigns(); void loadIdentities(); }, []);

  // Refresh campaign cards (stats) after each simulation tick
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const handler = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { void loadCampaigns({ silent: true }); }, 1500);
    };
    window.addEventListener(SIM_EVENT, handler);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener(SIM_EVENT, handler);
    };
  }, []);

  // Proxy campaigns update stats in the DB without SIM_EVENT — poll while any are running.
  useEffect(() => {
    if (!hasProxyRunning) return;
    const interval = window.setInterval(() => { void loadCampaigns({ silent: true }); }, PROXY_UI_POLL_MS);
    return () => window.clearInterval(interval);
  }, [hasProxyRunning]);

  // Close the card's "…" menu on any click outside it.
  useEffect(() => {
    if (!openMenuId) return;
    const handler = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest?.('[data-campaign-menu]')) setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openMenuId]);

  async function loadCampaigns(opts?: { silent?: boolean }) {
    if (!opts?.silent) setLoading(true);
    const data = await getCampaigns();
    if (data?.campaigns) {
      setCampaigns(data.campaigns);
      // Only a real (re)load re-sorts; silent polls keep the order the user is looking at.
      if (!opts?.silent) setOrderIds([...data.campaigns].sort(compareCampaigns).map(c => c._id));
    }
    if (!opts?.silent) setLoading(false);
  }

  async function loadIdentities() {
    // Own scope so this never cancels the settings dialog's fetch of the same list.
    const res: any = await getSenderIdentities(false, undefined, 'campaign-list');
    const list = res?.data?.identities ?? res?.identities;
    if (Array.isArray(list)) setIdentities(list);
    setIdentitiesLoaded(true);
    void loadDisconnectedAccounts();
  }

  // The live listing asks the provider about every account, so it runs after the list
  // is on screen; until it answers no card is flagged.
  async function loadDisconnectedAccounts() {
    const res: any = await getSenderIdentities(true, undefined, 'campaign-list-live');
    const list: ISenderIdentity[] = res?.data?.identities ?? res?.identities ?? [];
    setDisconnectedAccountIds(new Set(list
      .filter(i => i.linkedin?.accountId && isAccountDisconnected(i.linkedin.status))
      .map(i => String(i.linkedin.accountId))));
  }

  async function handleStatusChange(campaignId: string, status: CampaignStatus) {
    if (statusChangingId) return;
    const previous = campaigns.find(c => c._id === campaignId)?.status;
    setStatusChangingId(campaignId);
    try {
      const data = await updateCampaign(campaignId, { status });
      if (data?.campaign) {
        setCampaigns(prev => prev.map(c =>
          c._id === campaignId ? { ...data.campaign, statusCounts: c.statusCounts } : c
        ));
        const message = status === 'paused' ? 'Campaign paused.'
          : status === 'canceled' ? 'Campaign stopped.'
          : previous === 'paused' ? 'Campaign resumed.'
          : previous === 'canceled' ? 'Campaign restarted.'
          : 'Campaign started.';
        setToast({ type: 'success', message });
      } else {
        setToast({ type: 'error', message: (data as any)?.message || 'Could not update campaign status.' });
      }
    } finally {
      setStatusChangingId(null);
    }
  }

  async function handleRename() {
    if (!rename) return;
    const name = rename.name.trim();
    if (!name) return;
    const data = await updateCampaign(rename.id, { name });
    if (data?.campaign) {
      setCampaigns(prev => prev.map(c => (c._id === rename.id ? { ...c, name: data.campaign.name } : c)));
      setRename(null);
    } else {
      setToast({ type: 'error', message: (data as any)?.message || 'Could not rename campaign.' });
    }
  }

  async function handleDelete() {
    if (!deleteId) return;
    await deleteCampaign(deleteId);
    setCampaigns(prev => prev.filter(c => c._id !== deleteId));
    setDeleteId(null);
    setToast({ type: 'success', message: 'Campaign deleted.' });
  }

  function toggleSender(id: string) {
    setSenderFilter(prev => (prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]));
  }

  function clearFilters() {
    setSearchQuery('');
    setStatusFilter('all');
    setSenderFilter([]);
  }

  return (
    <Container>
      <CampaignFilterDropdownGlobalStyles />
      <PageHeader alignItems spaceBetween>
        <PageTitle>Campaigns</PageTitle>
        <Row alignItems gap="10px">
          {!hideSimUI && (
            <SimControls>
              <SimToggle
                $active={simConfig.enabled}
                type="button"
                title={simConfig.enabled ? 'Stop simulation' : 'Start simulation'}
                onClick={() => setSimConfig(c => ({ ...c, enabled: !c.enabled }))}
              >
                ⚙ {simConfig.enabled ? 'Simulating' : 'Simulate'}
              </SimToggle>
              {simConfig.enabled && (
                <SimParams>
                  <SimInput
                    type="number" min={5} max={86400} value={simConfig.minIntervalSeconds}
                    title="Min interval (seconds)"
                    onChange={e => setSimConfig(c => ({ ...c, minIntervalSeconds: +e.target.value }))}
                  />
                  <span>–</span>
                  <SimInput
                    type="number" min={5} max={86400} value={simConfig.maxIntervalSeconds}
                    title="Max interval (seconds)"
                    onChange={e => setSimConfig(c => ({ ...c, maxIntervalSeconds: +e.target.value }))}
                  />
                  <span>s &nbsp;|&nbsp;</span>
                  <SimInput
                    type="number" min={1} max={50} value={simConfig.minLeadsPerCycle}
                    title="Min leads/cycle (N)"
                    onChange={e => setSimConfig(c => ({ ...c, minLeadsPerCycle: +e.target.value }))}
                  />
                  <span>–</span>
                  <SimInput
                    type="number" min={1} max={50} value={simConfig.maxLeadsPerCycle}
                    title="Max leads/cycle (K)"
                    onChange={e => setSimConfig(c => ({ ...c, maxLeadsPerCycle: +e.target.value }))}
                  />
                  <span>leads</span>
                </SimParams>
              )}
            </SimControls>
          )}
          <TooltipWrapper tooltipText="Campaign settings" placement="bottom">
            <SettingsButton type="button" aria-label="Campaign settings" onClick={() => navigate(AppRoutes.SETTINGS_CAMPAIGNS)}>
              <Settings size={18} strokeWidth={1.75} />
            </SettingsButton>
          </TooltipWrapper>
          <NewButton type="button" onClick={() => navigate(AppRoutes.CAMPAIGN_NEW)}>
            <Plus size={16} strokeWidth={2.25} />
            Create new campaign
          </NewButton>
        </Row>
      </PageHeader>

      <TabsBar role="tablist">
        <TabButton type="button" role="tab" aria-selected={tab === 'campaigns'} $active={tab === 'campaigns'} onClick={() => setTab('campaigns')}>
          Campaigns
        </TabButton>
        <TabButton type="button" role="tab" aria-selected={tab === 'performance'} $active={tab === 'performance'} onClick={() => setTab('performance')}>
          Performance
        </TabButton>
      </TabsBar>

      {tab === 'performance' ? (
        <CampaignPerformance
          campaigns={campaigns}
          identities={identities}
          identitiesLoaded={identitiesLoaded}
          currentUserId={currentUserId}
        />
      ) : (
      <Panel>
        <PanelHeader>
        <PanelTitleRow alignItems gap="8px">
          <PanelTitle>All campaigns</PanelTitle>
          {!loading && <CountChip>{campaigns.length}</CountChip>}
        </PanelTitleRow>

        {!loading && campaigns.length > 0 && (
          <FilterToolbar alignItems gap="8px">
            <SearchWrap>
              <SearchIconBox aria-hidden><Search size={16} strokeWidth={1.33} /></SearchIconBox>
              <SearchInput
                type="search"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search campaigns"
                aria-label="Search campaigns by name, description, or creator"
              />
              {searchQuery && (
                <SearchClear type="button" onClick={() => setSearchQuery('')} aria-label="Clear search">×</SearchClear>
              )}
            </SearchWrap>
            <StatusFilterWrap>
              <StatusFilterDropdown
                className="campaign-status-filter sidebar-actions-v2"
                align="start"
                $hideToggle
                hideArrow
                show={statusMenuOpen}
                onToggle={setStatusMenuOpen}
                tooltipMessage="Filter by status"
                title={(
                  <FilterTrigger>
                    <FilterTriggerLabel>
                      <SlidersHorizontal size={14} />
                      <span>{activeStatusLabel}</span>
                    </FilterTriggerLabel>
                    <FilterChevron $open={statusMenuOpen}>
                      <ChevronDown size={16} />
                    </FilterChevron>
                  </FilterTrigger>
                )}
              >
                <FilterMenuPanel>
                  {STATUS_FILTER_OPTIONS.map(opt => (
                    <FilterMenuItem
                      key={opt.value}
                      onClick={() => {
                        setStatusFilter(opt.value);
                        setStatusMenuOpen(false);
                      }}
                    >
                      <FilterMenuRow>
                        <FilterMenuIcon>
                          {statusFilter === opt.value ? <Check size={14} strokeWidth={2.5} /> : null}
                        </FilterMenuIcon>
                        <span>{opt.label}</span>
                      </FilterMenuRow>
                    </FilterMenuItem>
                  ))}
                </FilterMenuPanel>
              </StatusFilterDropdown>
            </StatusFilterWrap>
            {identities.length > 0 && (
              <StatusFilterWrap>
                <SendersFilterDropdown
                  className="campaign-status-filter sidebar-actions-v2"
                  align="start"
                  $hideToggle
                  hideArrow
                  show={senderMenuOpen}
                  autoClose="outside"
                  onToggle={setSenderMenuOpen}
                  tooltipMessage="Filter by sender"
                  title={(
                    <FilterTrigger $senders>
                      <FilterTriggerLabel>
                        <Users size={20} strokeWidth={1.5} />
                        <span>Senders: {activeSenderLabel}</span>
                      </FilterTriggerLabel>
                      <FilterChevron $open={senderMenuOpen}>
                        <ChevronDown size={20} strokeWidth={1.67} />
                      </FilterChevron>
                    </FilterTrigger>
                  )}
                >
                  <FilterMenuPanel>
                    <FilterMenuItem onClick={() => setSenderFilter([])}>
                      <FilterMenuRow>
                        <FilterMenuIcon>
                          {senderFilter.length === 0 ? <Check size={14} strokeWidth={2.5} /> : null}
                        </FilterMenuIcon>
                        <span>All senders</span>
                      </FilterMenuRow>
                    </FilterMenuItem>
                    {identities.map(identity => (
                      <FilterMenuItem key={identity._id} onClick={() => toggleSender(identity._id)}>
                        <FilterMenuRow>
                          <FilterMenuIcon>
                            {senderFilter.includes(identity._id) ? <Check size={14} strokeWidth={2.5} /> : null}
                          </FilterMenuIcon>
                          <span>{senderLabel(String(identity._id))}</span>
                        </FilterMenuRow>
                      </FilterMenuItem>
                    ))}
                  </FilterMenuPanel>
                </SendersFilterDropdown>
              </StatusFilterWrap>
            )}
            {hasActiveFilters && (
              <FilterMeta>
                {filteredCampaigns.length} of {campaigns.length} campaign{filteredCampaigns.length === 1 ? '' : 's'}
              </FilterMeta>
            )}
          </FilterToolbar>
        )}
        </PanelHeader>

        {loading ? (
          <LoadingBox aria-busy="true" aria-label="Loading campaigns">
            <LoadingSpinner height="28px" width="28px" />
          </LoadingBox>
        ) : campaigns.length === 0 ? (
          <EmptyState>
            <SvgFragment type="sideCampaignsLucide" />
            <Text $bold style={{ marginTop: '12px' }}>No campaigns yet</Text>
            <Text $paragraph2 style={{ color: styles.colors.black300, marginTop: '4px' }}>
              Create your first campaign to start your outreach.
            </Text>
          </EmptyState>
        ) : filteredCampaigns.length === 0 ? (
          <EmptyState>
            <SvgFragment type="searchEmpty" />
            <Text $bold style={{ marginTop: '12px' }}>No matching campaigns</Text>
            <Text $paragraph2 style={{ color: styles.colors.black300, marginTop: '4px' }}>
              Try a different search term or filter.
            </Text>
            {hasActiveFilters && (
              <ClearFiltersBtn type="button" onClick={clearFilters}>
                Clear filters
              </ClearFiltersBtn>
            )}
          </EmptyState>
        ) : (
          <CardsGrid>
            {filteredCampaigns.map(c => {
            const isOwner = !!currentUserId && String(c.createdBy) === String(currentUserId);
            const manageTooltip = isOwner ? '' : `Only ${c.createdByName || 'the campaign creator'} can manage this campaign.`;
            const leadTotal = sumEnrolledLeads(c.statusCounts);
            // A name-only draft has no leads table to show. Open the wizard on the
            // settings step so the user continues setup instead of an empty leads page.
            const openWizard = c.status === 'draft' && leadTotal === 0;
            const senderNames = campaignSenderIds(c).map(id => identityNames.get(id)).filter((n): n is string => !!n);
            const sourceLabel = c.leadSource && c.leadSource !== 'empty' ? SOURCE_LABELS[c.leadSource] : '';
            const meta = [
              senderNames.length ? `Sender: ${formatSenderNames(senderNames)}` : '',
              sourceLabel,
              formatUpdatedAgo(c.updatedAt),
            ].filter(Boolean).join(' · ');
            const menuOpen = openMenuId === c._id;
            // An empty campaign can go live — it waits for leads instead of sending.
            const emptyStartHint = leadTotal === 0 ? CAMPAIGN_START_NO_LEADS_HINT : '';
            return (
              <CampaignCard key={c._id} $status={c.status} onClick={() => navigate(openWizard ? `/campaigns/${c._id}/edit` : `/campaigns/${c._id}/leads`)}>
                <CardTop spaceBetween>
                  <CardTitleRow>
                    <CardTitle>{c.name}</CardTitle>
                    <StatusBadge $status={c.status}>{STATUS_LABELS[c.status]}</StatusBadge>
                    {/* Only where it matters: a live proxy campaign that has lost an account. */}
                    {c.executionMethod === 'proxy' && (c.status === 'active' || c.status === 'paused')
                      && (c.unipileConfig?.accountIds ?? []).some(a => disconnectedAccountIds.has(String(a))) && (
                      <TooltipWrapper tooltipText="A LinkedIn account in this campaign is disconnected, so it can't send. Open the campaign to reconnect." placement="top">
                        <DisconnectedBadge>
                          <TriangleAlert size={12} strokeWidth={2} />
                          Account disconnected
                        </DisconnectedBadge>
                      </TooltipWrapper>
                    )}
                  </CardTitleRow>

                  <ActionRow alignItems gap="6px" onClick={e => e.stopPropagation()}>
                    {c.status === 'active' && (
                      <TooltipWrapper tooltipText={manageTooltip || 'Pause. Resume anytime.'} placement="top">
                        <TooltipBtnWrap>
                          <StatusActionButton
                            type="button" $variant="warning" aria-label="Pause campaign"
                            aria-busy={statusChangingId === c._id}
                            disabled={!isOwner || !!statusChangingId}
                            onClick={() => handleStatusChange(c._id, 'paused')}
                          >
                            {statusChangingId === c._id ? <SpinIcon size={14} strokeWidth={2} /> : <Pause size={14} strokeWidth={1.75} />}
                          </StatusActionButton>
                        </TooltipBtnWrap>
                      </TooltipWrapper>
                    )}
                    {/* Drafts get no button — the card itself opens them for editing. */}
                    {(c.status === 'paused' || c.status === 'canceled') && (
                      <TooltipWrapper tooltipText={manageTooltip || emptyStartHint || (c.status === 'paused' ? 'Resume' : 'Restart')} placement="top">
                        <TooltipBtnWrap>
                          <StatusActionButton
                            type="button" $variant="success" aria-label={c.status === 'paused' ? 'Resume campaign' : 'Restart campaign'}
                            aria-busy={statusChangingId === c._id}
                            disabled={!isOwner || !!statusChangingId}
                            onClick={() => handleStatusChange(c._id, 'active')}
                          >
                            {statusChangingId === c._id ? <SpinIcon size={14} strokeWidth={2} /> : <Play size={14} strokeWidth={1.75} />}
                          </StatusActionButton>
                        </TooltipBtnWrap>
                      </TooltipWrapper>
                    )}
                    <MenuAnchor data-campaign-menu>
                      <MenuToggle
                        type="button" aria-label="More actions" aria-expanded={menuOpen}
                        $open={menuOpen}
                        onClick={() => setOpenMenuId(menuOpen ? null : c._id)}
                      >
                        <Ellipsis size={16} strokeWidth={1.75} />
                      </MenuToggle>
                      {menuOpen && (
                        <CardMenu role="menu">
                          <TooltipWrapper tooltipText={manageTooltip} placement="left">
                            <CardMenuItem
                              type="button" role="menuitem" disabled={!isOwner}
                              onClick={() => { setOpenMenuId(null); setRename({ id: c._id, name: c.name }); }}
                            >
                              <Pencil size={15} />
                              Rename
                            </CardMenuItem>
                          </TooltipWrapper>
                          {/* Same rule as the campaign page's Edit: a running or stopped
                              campaign can't be edited. */}
                          <TooltipWrapper
                            tooltipText={manageTooltip
                              || (c.status === 'active' ? 'Pause the campaign to edit settings.'
                                : c.status === 'canceled' ? 'Stopped campaigns can’t be edited.'
                                : '')}
                            placement="left"
                          >
                            <CardMenuItem
                              type="button" role="menuitem"
                              disabled={!isOwner || c.status === 'active' || c.status === 'canceled'}
                              onClick={() => { setOpenMenuId(null); navigate(`/campaigns/${c._id}/edit`); }}
                            >
                              <EditIcon />
                              Edit
                            </CardMenuItem>
                          </TooltipWrapper>
                          <TooltipWrapper tooltipText={manageTooltip || (c.status === 'canceled' ? 'This campaign is already stopped.' : 'Stops all outreach. You can restart it later.')} placement="left">
                            <CardMenuItem
                              type="button" role="menuitem" disabled={!isOwner || c.status === 'canceled'}
                              onClick={() => { setOpenMenuId(null); setStopId(c._id); }}
                            >
                              <Ban size={15} />
                              Stop
                            </CardMenuItem>
                          </TooltipWrapper>
                          <TooltipWrapper tooltipText={manageTooltip} placement="left">
                            <CardMenuItem
                              type="button" role="menuitem" $danger disabled={!isOwner}
                              onClick={() => { setOpenMenuId(null); setDeleteId(c._id); }}
                            >
                              <Trash2 size={15} />
                              Delete
                            </CardMenuItem>
                          </TooltipWrapper>
                        </CardMenu>
                      )}
                    </MenuAnchor>
                  </ActionRow>
                </CardTop>

                <CardBody>
                  {meta && <CardMeta>{meta}</CardMeta>}
                  <StatsRow>
                    {buildStats(c).map(stat => (
                      <StatBox key={stat.key}>
                        <StatValue>{stat.value}</StatValue>
                        <StatLabel>{stat.label}</StatLabel>
                      </StatBox>
                    ))}
                  </StatsRow>
                </CardBody>
              </CampaignCard>
            );
            })}
          </CardsGrid>
        )}
      </Panel>
      )}

      {/* Rename */}
      {rename && (
        <Overlay onClick={() => setRename(null)}>
          <DialogCard onClick={e => e.stopPropagation()}>
            <Text $bold>Rename campaign</Text>
            <RenameInput
              autoFocus
              value={rename.name}
              maxLength={120}
              onChange={e => setRename({ ...rename, name: e.target.value })}
              onKeyDown={e => {
                if (e.key === 'Enter') void handleRename();
                if (e.key === 'Escape') setRename(null);
              }}
              aria-label="Campaign name"
            />
            <Row gap="8px" style={{ justifyContent: 'flex-end', marginTop: '20px' }}>
              <CancelButton type="button" onClick={() => setRename(null)}>Cancel</CancelButton>
              <PrimaryButton type="button" disabled={!rename.name.trim()} onClick={() => void handleRename()}>Save</PrimaryButton>
            </Row>
          </DialogCard>
        </Overlay>
      )}

      {/* Stop confirmation */}
      {stopId && (
        <Overlay onClick={() => setStopId(null)}>
          <DialogCard onClick={e => e.stopPropagation()}>
            <StopIconWrap><SvgFragment type="campaignStop" /></StopIconWrap>
            <StopModalTitle>Stop this campaign?</StopModalTitle>
            <StopModalBody>
              Once stopped, this campaign <StopModalHighlight>sends nothing</StopModalHighlight> until you restart it.
            </StopModalBody>
            <Row gap="8px" style={{ justifyContent: 'flex-end', marginTop: '28px' }}>
              <CancelButton type="button" onClick={() => setStopId(null)}>Keep running</CancelButton>
              <DangerButton type="button" onClick={async () => { await handleStatusChange(stopId, 'canceled'); setStopId(null); }}>
                <SvgFragment type="campaignStop" />
                Stop campaign
              </DangerButton>
            </Row>
          </DialogCard>
        </Overlay>
      )}

      {/* Delete confirmation */}
      {deleteId && (
        <Overlay onClick={() => setDeleteId(null)}>
          <DialogCard onClick={e => e.stopPropagation()}>
            <Text $bold>Delete campaign?</Text>
            <Text $paragraph2 style={{ color: styles.colors.black300, marginTop: '8px' }}>
              This action cannot be undone. The campaign and all its data will be permanently removed.
            </Text>
            <Row gap="8px" style={{ justifyContent: 'flex-end', marginTop: '20px' }}>
              <CancelButton type="button" onClick={() => setDeleteId(null)}>Cancel</CancelButton>
              <DangerButton type="button" onClick={handleDelete}>Delete</DangerButton>
            </Row>
          </DialogCard>
        </Overlay>
      )}
    </Container>
  );
};

// ---- Styled Components ----

const Container = styled.div`
  width: 100%; padding: 0 24px 32px;
  display: flex; flex-direction: column; gap: 12px;
  height: 100%; overflow-y: auto;
  ${campaignMobile`padding: 0 12px 24px; gap: 10px;`}
`;

const PageHeader = styled(Row)`
  padding-top: 16px;
  ${campaignMobile`
    padding-top: 12px;
    flex-direction: column;
    align-items: flex-start !important;
    gap: 12px;
  `}
`;

const PageTitle = styled.h1`
  margin: 0;
  font-family: NunitoSans; font-size: 20px; font-weight: 700; line-height: 28px;
  color: ${styles.colors.black600};
  ${campaignMobile`font-size: 18px; line-height: 26px;`}
`;

const TabsBar = styled.div`
  display: flex; gap: 24px;
  border-bottom: 1px solid #e8eaed;
`;

const TabButton = styled.button<{ $active: boolean }>`
  position: relative;
  padding: 0 4px 12px;
  border: none; background: transparent; cursor: pointer;
  font-family: NunitoSans; font-size: 14px; font-weight: 600; line-height: 20px;
  color: ${({ $active }) => ($active ? styles.colors.primary600 : styles.colors.black400)};

  &::after {
    content: '';
    position: absolute; left: 0; right: 0; bottom: -1px;
    height: 2px; border-radius: 2px;
    background: ${({ $active }) => ($active ? styles.colors.primary600 : 'transparent')};
  }

  &:hover { color: ${styles.colors.primary600}; }
`;

// Card typography follows the design spec (Inter); NunitoSans covers machines without it.
const INTER = `'Inter', NunitoSans, sans-serif`;

const Panel = styled.div`
  background: ${styles.colors.white};
  border: 1px solid #e9eaeb;
  border-radius: 18px;
  padding: 24px;
  display: flex; flex-direction: column; gap: 16px;
  ${campaignMobile`padding: 16px;`}
`;

const PanelHeader = styled.div`
  display: flex; flex-direction: column; justify-content: center; gap: 24px;
  ${campaignMobile`gap: 16px;`}
`;

const PanelTitleRow = styled(Row)``;

const PanelTitle = styled.span`
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 20px; font-weight: 700; line-height: 30px;
  color: #111927;
`;

const CountChip = styled.span`
  padding: 2px 8px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 9999px;
  background: #e7edff; color: #423bc0;
  font-family: ${INTER}; font-size: 12px; font-weight: 500; line-height: 16px;
`;

const FilterToolbar = styled(Row)`
  flex-wrap: wrap;
  ${campaignMobile`
    flex-direction: column;
    align-items: stretch !important;
  `}
`;

const SearchWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 288px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #dae0e8;
  border-radius: 10px;
  background: ${styles.colors.white};
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus-within {
    border-color: ${styles.colors.primary600};
    box-shadow: 0 0 0 3px rgba(89, 100, 255, 0.08);
  }

  ${campaignMobile`width: 100%;`}
`;

const SearchIconBox = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  line-height: 0;
  color: #8c96ad;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  font-family: ${INTER};
  font-size: 14px;
  line-height: 17px;
  letter-spacing: -0.15px;
  color: #333951;

  &::placeholder { color: #677284; }
  &::-webkit-search-cancel-button { display: none; }
`;

const SearchClear = styled.button`
  border: none;
  background: transparent;
  color: ${styles.colors.black300};
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;

  &:hover { color: ${styles.colors.black600}; }
`;

const StatusFilterWrap = styled.div`
  flex-shrink: 0;
  ${campaignMobile`width: 100%;`}
`;

const CampaignFilterDropdownGlobalStyles = createGlobalStyle`
  .campaign-status-filter .actions-dropdown + div,
  .campaign-status-filter .dropdown-menu {
    padding: 0;
    border: 1px solid #e8eaed;
    border-radius: 10px;
    box-shadow: 0px 0px 6px rgba(51, 57, 81, 0.1);
    background: #fff;
    min-width: 190px;
    width: max-content;
    max-height: 320px !important;
    overflow-y: auto !important;
  }

  .campaign-status-filter .dropdown-toggle::after,
  .campaign-status-filter .dropdown-toggle::before {
    display: none !important;
    content: none !important;
  }
`;

const StatusFilterDropdown = styled(DropdownMenu)`
  &.dropdown {
    border: 1px solid #e1e5eb;
    border-radius: 10px;
    background: ${styles.colors.white};
    transition: border-color 0.15s, box-shadow 0.15s;

    .dropdown-toggle {
      height: 34px;
      min-width: 170px;
      padding: 0 10px 0 12px;
      border-radius: 10px;
      width: 100%;
    }

    &.show,
    &:hover {
      box-shadow: 0 0 0 3px rgba(89, 100, 255, 0.08);
    }
  }

  ${campaignMobile`
    &.dropdown { width: 100%; }
    .dropdown-toggle { min-width: 0; width: 100%; }
  `}
`;

// Fixed width so the trigger doesn't jump as the selection changes; long names truncate.
const SendersFilterDropdown = styled(DropdownMenu)`
  &.dropdown {
    width: 283px;
    border: 1px solid #dae0e8;
    border-radius: 8px;
    background: ${styles.colors.white};
    transition: border-color 0.15s, box-shadow 0.15s;

    .dropdown-toggle {
      height: 34px;
      padding: 0 14px;
      border-radius: 8px;
      width: 100%;
    }

    .dropdownWrapperReference { width: 100%; min-width: 0; }

    &.show,
    &:hover {
      box-shadow: 0 0 0 3px rgba(89, 100, 255, 0.08);
    }
  }

  ${campaignMobile`
    &.dropdown { width: 100%; }
    .dropdown-toggle { width: 100%; }
  `}
`;

const FilterTrigger = styled.div<{ $senders?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ $senders }) => ($senders ? '8px' : '10px')};
  width: 100%;
  font-family: 'Nunito Sans', NunitoSans, sans-serif;
  font-size: ${({ $senders }) => ($senders ? '14px' : '13px')};
  font-weight: ${({ $senders }) => ($senders ? 400 : 600)};
  line-height: 20px;
  color: #333951;
`;

const FilterTriggerLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  svg { flex-shrink: 0; color: #8c96ad; }

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 220px;
  }
`;

const FilterChevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: #8c96ad;
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
`;

const FilterMenuPanel = styled.div`
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 0;
  font-family: 'Nunito Sans', sans-serif;
`;

const FilterMenuItem = styled.div`
  cursor: pointer;
  user-select: none;
  padding: 6px;
  border-radius: 8px;
  transition: background-color 0.15s ease;

  &:hover { background-color: #f3f4f6; }
`;

const FilterMenuRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #41495e;

  span { white-space: nowrap; }
`;

const FilterMenuIcon = styled.span`
  width: 14px;
  height: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${styles.colors.primary600};
`;

const FilterMeta = styled.span`
  font-family: NunitoSans;
  font-size: 12px;
  color: ${styles.colors.black300};
  white-space: nowrap;
`;

const ClearFiltersBtn = styled.button`
  margin-top: 12px;
  height: 34px;
  padding: 0 14px;
  border: 1px solid ${styles.colors.black200};
  border-radius: 8px;
  background: ${styles.colors.white};
  color: ${styles.colors.primary600};
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &:hover { background: ${styles.colors.primary100}; }
`;

const NewButton = styled.button`
  display: flex; align-items: center; gap: 8px;
  height: 34px; padding: 0 14px;
  background-color: ${styles.colors.primary600}; color: ${styles.colors.white};
  border: none; border-radius: 8px; font-size: 14px; font-weight: 600;
  cursor: pointer; font-family: NunitoSans; white-space: nowrap; flex-shrink: 0;
  &:hover { background-color: ${styles.colors.primary400}; }
  ${campaignMobile`width: 100%; justify-content: center;`}
`;

// Sits beside "Create new campaign": same height, quieter, so the main action stays the main one.
const SettingsButton = styled.button`
  display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
  width: 40px; height: 40px; padding: 0;
  background: ${styles.colors.white}; color: ${styles.colors.black500};
  border: 1px solid #dde3ee; border-radius: 8px;
  cursor: pointer; transition: background 0.15s, color 0.15s, border-color 0.15s;
  &:hover { background: ${styles.colors.primary100}; color: ${styles.colors.primary600}; border-color: ${styles.colors.primary200}; }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px ${styles.colors.primary100}; border-color: ${styles.colors.primary600}; }
`;

const LoadingBox = styled.div`
  display: flex; align-items: center; justify-content: center;
  padding: 60px 24px;
`;

const EmptyState = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 60px 24px; background: ${styles.colors.white};
  border: 1px dashed ${styles.colors.black200}; border-radius: 18px; color: ${styles.colors.black400};
  .svg { width: 40px; height: 40px; opacity: 0.3; }
`;

const CardsGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(480px, 1fr)); gap: 24px;
  ${campaignMobile`grid-template-columns: 1fr; gap: 12px;`}
`;

const CampaignCard = styled.div<{ $status?: CampaignStatus }>`
  background: ${styles.colors.white}; border: 1px solid #e1e5eb;
  border-radius: 18px; padding: 20px 20px 20px 24px;
  display: flex; flex-direction: column;
  transition: box-shadow 0.18s ease, border-color 0.18s ease;
  position: relative; cursor: pointer;
  ${campaignMobile`padding: 16px;`}
  &:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.06); }
`;

const CardTop = styled(Row)`gap: 12px; align-items: flex-start !important;`;

const CardTitleRow = styled.div`
  display: flex; align-items: center; gap: 8px; min-width: 0; flex: 1; height: 24px;
`;

const CardTitle = styled.span`
  font-family: ${INTER}; font-size: 16px; font-weight: 600; line-height: 24px;
  letter-spacing: -0.3125px; color: #111a2d;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const CardBody = styled.div`
  display: flex; flex-direction: column; gap: 16px;
`;

const CardMeta = styled.span`
  padding-top: 2px;
  font-family: ${INTER}; font-size: 12px; font-weight: 400; line-height: 16px;
  color: #677284;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const StatusBadge = styled.span<{ $status: CampaignStatus }>`
  flex-shrink: 0; padding: 4px 10px; border-radius: 9999px;
  font-family: ${INTER}; font-size: 10px; font-weight: 700; line-height: 15px;
  text-transform: uppercase; letter-spacing: 0.617px;
  ${({ $status }) => $status === 'active'   && `background: #d0fae5; color: #007a55;`}
  ${({ $status }) => $status === 'paused'   && `background: #fef3c6; color: #bb4d00;`}
  ${({ $status }) => $status === 'draft'    && `background: #eff2f9; color: #677284;`}
  ${({ $status }) => $status === 'canceled' && `background: ${styles.colors.red200}; color: ${styles.colors.red600};`}
`;

const DisconnectedBadge = styled.span`
  flex-shrink: 0; display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px; border-radius: 9999px;
  font-family: ${INTER}; font-size: 10px; font-weight: 700; line-height: 15px;
  text-transform: uppercase; letter-spacing: 0.617px;
  background: ${styles.colors.red200}; color: ${styles.colors.red600};
`;

const StatsRow = styled.div`
  display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 12px;
  ${campaignMobile`
    gap: 8px;
    grid-template-columns: repeat(5, minmax(64px, 1fr));
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  `}
`;

const StatBox = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  height: 65px; padding: 8px;
  background: ${styles.colors.white};
  border: 1px solid rgba(225, 229, 235, 0.6); border-radius: 10px;
  min-width: 0;
`;

const StatValue = styled.span`
  font-family: ${INTER}; font-size: 16px; font-weight: 700; line-height: 16px;
  letter-spacing: -0.3125px; text-align: center; color: #111a2d;
`;

const StatLabel = styled.span`
  padding-top: 4px;
  font-family: ${INTER}; font-size: 9px; font-weight: 500; line-height: 14px;
  text-transform: uppercase; letter-spacing: 0.617px; text-align: center; color: #677284;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%;
`;

const ActionRow = styled(Row)`
  flex-wrap: nowrap; flex-shrink: 0; cursor: default;
`;

const TooltipBtnWrap = styled.span`display: inline-flex;`;

const StatusActionButton = styled.button<{ $variant: 'success' | 'warning' }>`
  width: 34px; height: 32px; padding: 0 10px; border-radius: 8px; border: none;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer;
  ${({ $variant }) => $variant === 'success' && `background: #ecfdf5; color: #007a55;`}
  ${({ $variant }) => $variant === 'warning' && `background: #fef3c6; color: #bb4d00;`}
  &:hover:not(:disabled) { filter: brightness(0.95); }
  &:disabled { opacity: 0.38; cursor: default; }
  /* The one being changed stays at full strength, so it reads as working, not disabled. */
  &[aria-busy='true'] { opacity: 1; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SpinIcon = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

const MenuAnchor = styled.div`position: relative;`;

const MenuToggle = styled.button<{ $open: boolean }>`
  width: 32px; height: 32px; border-radius: 8px; border: none;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer; color: #677284;
  background: ${({ $open }) => ($open ? styles.colors.black100 : 'transparent')};
  &:hover { background: ${styles.colors.black100}; }
`;

const CardMenu = styled.div`
  position: absolute; top: calc(100% + 6px); right: 0; z-index: 20;
  min-width: 164px; padding: 6px;
  display: flex; flex-direction: column;
  background: ${styles.colors.white};
  border: 1px solid #e8eaed; border-radius: 10px;
  box-shadow: 0 6px 20px rgba(51, 57, 81, 0.12);
`;

const CardMenuItem = styled.button<{ $danger?: boolean }>`
  width: 100%;
  display: flex; align-items: center; gap: 10px;
  padding: 8px 10px; border: none; border-radius: 6px;
  background: transparent; cursor: pointer; text-align: left;
  font-family: NunitoSans; font-size: 14px;
  color: ${({ $danger }) => ($danger ? styles.colors.red600 : styles.colors.black600)};
  svg { flex-shrink: 0; color: ${({ $danger }) => ($danger ? styles.colors.red600 : styles.colors.black400)}; }
  &:hover:not(:disabled) { background: ${({ $danger }) => ($danger ? styles.colors.red200 : '#f3f4f6')}; }
  &:disabled { opacity: 0.38; cursor: default; }
`;

const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.35); z-index: 200;
  display: flex; align-items: center; justify-content: center; padding: 16px;
`;

const DialogCard = styled.div`
  background: ${styles.colors.white}; border-radius: 16px; padding: 28px;
  width: 100%; max-width: 400px; box-shadow: 0 20px 60px rgba(0,0,0,0.15);
`;

const RenameInput = styled.input`
  width: 100%; height: 40px; margin-top: 14px; padding: 0 12px;
  border: 1px solid #dde3ee; border-radius: 8px; outline: none;
  font-family: NunitoSans; font-size: 14px; color: ${styles.colors.black600};
  &:focus { border-color: ${styles.colors.primary600}; box-shadow: 0 0 0 3px rgba(89, 100, 255, 0.08); }
`;

const CancelButton = styled.button`
  height: 34px; padding: 0 14px; background: transparent; color: ${styles.colors.black400};
  border: 1px solid ${styles.colors.black200}; border-radius: 8px;
  font-size: 13px; font-weight: 600; font-family: NunitoSans; cursor: pointer;
  &:hover { background-color: ${styles.colors.black100}; }
`;

const PrimaryButton = styled.button`
  height: 34px; padding: 0 16px; background-color: ${styles.colors.primary600}; color: ${styles.colors.white};
  border: none; border-radius: 8px; font-size: 13px; font-weight: 700;
  font-family: NunitoSans; cursor: pointer;
  &:hover:not(:disabled) { background-color: ${styles.colors.primary400}; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const DangerButton = styled.button`
  height: 34px; padding: 0 16px; background-color: ${styles.colors.red600}; color: ${styles.colors.white};
  border: none; border-radius: 8px; font-size: 13px; font-weight: 700;
  font-family: NunitoSans; cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
  .svg { width: 13px; height: 13px; } .path { stroke: ${styles.colors.white} !important; fill: none !important; }
  &:hover { background-color: ${styles.colors.red500}; }
`;

const StopIconWrap = styled.div`
  width: 48px; height: 48px; border-radius: 50%; background: ${styles.colors.red200};
  display: flex; align-items: center; justify-content: center; margin-bottom: 16px;
  .svg { width: 22px; height: 22px; } .path { stroke: ${styles.colors.red600} !important; fill: none !important; }
`;

const StopModalTitle = styled.div`
  font-family: NunitoSans; font-size: 18px; font-weight: 800;
  color: ${styles.colors.black600}; margin-bottom: 10px;
`;

const StopModalBody = styled.p`
  font-family: NunitoSans; font-size: 14px; line-height: 22px;
  color: ${styles.colors.black300}; margin: 0;
`;

const StopModalHighlight = styled.span`font-weight: 700; color: ${styles.colors.red600};`;

const SimControls = styled.div`display: flex; align-items: center; gap: 8px;`;

const SimToggle = styled.button<{ $active: boolean }>`
  height: 32px; padding: 0 12px; border-radius: 20px; cursor: pointer;
  font-family: NunitoSans; font-size: 12px; font-weight: 700;
  border: 1.5px solid ${({ $active }) => $active ? styles.colors.orange600 : '#dde3ee'};
  background: ${({ $active }) => $active ? styles.colors.yellow200 : styles.colors.white};
  color: ${({ $active }) => $active ? styles.colors.orange600 : styles.colors.black400};
  transition: all 0.14s;
  &:hover { border-color: ${styles.colors.orange600}; color: ${styles.colors.orange600}; }
`;

const SimParams = styled.div`
  display: flex; align-items: center; gap: 4px;
  font-family: NunitoSans; font-size: 12px; color: ${styles.colors.black400};
`;

const SimInput = styled.input`
  width: 52px; height: 28px; padding: 0 6px; text-align: center;
  border: 1.5px solid #dde3ee; border-radius: 6px;
  font-family: NunitoSans; font-size: 12px; color: ${styles.colors.black600};
  outline: none; background: ${styles.colors.white};
  &:focus { border-color: ${styles.colors.primary600}; }
`;

export default CampaignSection;
